/**
 * Which steps of a check-in are done, and which are still outstanding.
 *
 * The session detail page is what staff sit on while a check-in runs, and its
 * panels are the steps: customer details, devices, terms and payment.
 * Before this, nothing on the page said which of them still wanted
 * something — an operator had to open each panel and read it. So each tracked
 * panel carries a state, the outstanding ones are marked loudly and the
 * finished ones collapse themselves (`CollapsibleSection`), leaving what is
 * left to do as what is open on screen.
 *
 * **Each panel judges itself from its own data, not from the lifecycle.** A
 * lifecycle status is the session's overall position and it can run ahead of
 * what the panels hold: the wall flow creates a session with no devices and
 * the customer can accept the terms seconds later, so `terms_accepted` would
 * otherwise mark an empty device list as done. The panel reads done only when
 * the thing the panel is for actually exists.
 *
 * `null` means "no mark on this panel", used for two different reasons:
 *  - the step does not apply to this session (express collects no terms; a
 *    `not_required` payment has no panel at all), and
 *  - the session is terminal, where a `todo` is demoted to `null` — nothing is
 *    outstanding on a check-in that is finished, cancelled or expired, and
 *    there is nothing an operator could do about it if it were. A step whose
 *    data *is* satisfied still reads Done on a terminal session, because that
 *    is a fact about it.
 *
 * Reference panels (Session, Preliminary Charges, Print, Audit) are not steps
 * and are deliberately left unmarked.
 */

import type { CheckInSession } from '@techgitter/contracts';

import { TERMINAL_LIFECYCLES, isPaymentSettled } from './sessionDetailHelpers';

export type SectionStatus = 'done' | 'todo';

export interface CheckInSectionProgress {
  customerDetails: SectionStatus | null;
  devices: SectionStatus | null;
  print: SectionStatus | null;
  terms: SectionStatus | null;
  payment: SectionStatus | null;
}

/**
 * Customer details are done once we can both name the customer and reach
 * them. Either contact method counts: an online intake needs an email or a
 * mobile to deliver the link, and reception often has only one of them.
 */
function customerDetailsStatus(session: CheckInSession): SectionStatus {
  const name = (
    session.customerName
    ?? [session.customerFirstName, session.customerLastName].filter(Boolean).join(' ')
  )?.trim();
  const reachable = !!session.customerEmail?.trim() || !!session.customerPhone?.trim();
  return name && reachable ? 'done' : 'todo';
}

/**
 * The device list is done when there is at least one device and every one of
 * them has been inspected. This is the same condition `complete_session`
 * enforces server-side, so the panel cannot read done while Complete Check-In
 * would still refuse.
 */
function devicesStatus(session: CheckInSession): SectionStatus {
  const draftJobs = session.draftJobs ?? [];
  if (draftJobs.length === 0) return 'todo';
  return draftJobs.every(job => job.inspectionStatus === 'complete') ? 'done' : 'todo';
}

/**
 * Printing is done once a document has actually gone to a printer, which
 * sm4rt-api records as a `document_printed` audit row (published prints only —
 * a skipped one records nothing).
 *
 * **`null`, never `'todo'`.** Printing is not a step of every check-in: the
 * AutoPrint binding usually sends the paperwork at completion without anyone
 * pressing anything, so an amber "To do" would claim work that is not owed.
 * The mark only ever appears once there is something to report.
 */
function printStatus(session: CheckInSession): SectionStatus | null {
  const printed = (session.audit ?? []).some(
    e => (e.eventType as string) === 'document_printed',
  );
  return printed ? 'done' : null;
}

/**
 * Terms are the customer's to accept, from their own phone or the reception
 * TV — or, when they can't, handed to them on paper ("Continue without digital
 * acceptance"), which settles the step without being an acceptance. Express
 * has no customer-facing intake at all, so it is unmarked rather than
 * permanently outstanding.
 */
function termsStatus(session: CheckInSession): SectionStatus | null {
  if (session.intakeType === 'express') return null;
  return session.termsAcceptance || session.termsNotice ? 'done' : 'todo';
}

/** Unmarked when no payment is due — the page hides the panel in that case. */
function paymentStatus(session: CheckInSession): SectionStatus | null {
  const state = session.paymentState as string | null | undefined;
  if (!state) return null;
  // `not_required` from the start (express, returns) hides the panel; chosen
  // by reception it is a decision, and the panel stays to say who made it.
  if (state === 'not_required' && !session.paymentOverriddenBy) return null;
  return isPaymentSettled(session) ? 'done' : 'todo';
}

export function isTerminalSession(session: CheckInSession | null): boolean {
  return TERMINAL_LIFECYCLES.has((session?.lifecycleStatus ?? '') as string);
}

export function deriveSectionProgress(session: CheckInSession | null): CheckInSectionProgress {
  if (!session) {
    return {
      customerDetails: null,
      devices: null,
      print: null,
      terms: null,
      payment: null,
    };
  }

  const progress: CheckInSectionProgress = {
    customerDetails: customerDetailsStatus(session),
    devices: devicesStatus(session),
    print: printStatus(session),
    terms: termsStatus(session),
    payment: paymentStatus(session),
  };

  if (!isTerminalSession(session)) return progress;

  // Terminal: keep what was achieved, drop what is merely unfinished.
  return Object.fromEntries(
    Object.entries(progress).map(([key, status]) => [key, status === 'todo' ? null : status]),
  ) as unknown as CheckInSectionProgress;
}
